import { DeleteOutline, Info } from "@mui/icons-material";
import {
  Box,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DynamicContentGallery } from "@platformx/authoring-utilities";
import { replace, upload } from "@platformx/shared/static-assets";
import { capitalizeFirstLetter } from "@platformx/utilities";
import { useCallback, useEffect, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InternalPageList from "../InternalPageList/InternalPageList";

interface ContentItem {
  Title: string;
  content_path: string;
  content_name: string;
  CurrentPageURL: string;
  content_type: string;
  is_hidden: boolean;
  type: string;
}

interface ButtonValue {
  display_name: string;
  selected_option: "page" | "link" | "content" | "function";
  link_url: string;
  page_id: string;
  content: ContentItem | null;
  function_name: string;
  open_in_new_tab: boolean;
}

interface ButtonFieldProps {
  field: {
    name: string;
    title: string;
    description?: string;
    validations?: Array<{
      type: string;
      value?: number;
      message: string;
    }>;
    default_value?: Partial<ButtonValue>;
  };
  state: ButtonValue;
  setState: (value: ButtonValue) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  isDisabled?: boolean;
}

// Error keys used in the validation message (JSON-encoded)
interface ButtonFieldErrors {
  display_name?: string;
  link_url?: string;
  page_id?: string;
  content?: string;
  function_name?: string;
}

const DEFAULT_BUTTON_VALUE: ButtonValue = {
  display_name: "",
  selected_option: "link",
  link_url: "",
  page_id: "",
  content: null,
  function_name: "",
  open_in_new_tab: true,
};

const SELECTED_OPTION_LIST = [
  { label: "Page", value: "page" },
  { label: "Link", value: "link" },
  { label: "Content", value: "content" },
  { label: "Function", value: "function" },
];

const TAB_OPTIONS = [
  { label: "New Tab", value: "new_tab" },
  { label: "Current Tab", value: "current_tab" },
];

const AVAILABLE_FUNCTIONS = [
  {
    value: "cookie_policy",
    label: "Cookie Policy",
    info: "This function will open the cookie policy dialog.",
  },
  {
    value: "subscribe",
    label: "Subscribe",
    info: "This function will trigger the subscription workflow.",
  },
];

/**
 * Parse the JSON-encoded error message back into per-field errors.
 * Returns an empty object when there is no error or parsing fails.
 */
const parseFieldErrors = (errorMessage?: string): ButtonFieldErrors => {
  if (!errorMessage) return {};
  try {
    return JSON.parse(errorMessage) as ButtonFieldErrors;
  } catch {
    return {};
  }
};

const ButtonField = ({ field, state, setState, control, isDisabled = false }: ButtonFieldProps) => {
  const { t } = useTranslation();
  const dropDownMenuRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeRef = useRef<(value: any) => void>(() => {});
  const buttonDataRef = useRef<ButtonValue>(DEFAULT_BUTTON_VALUE);

  const [contentGalleryOpen, setContentGalleryOpen] = useState(false);
  const [selectedContentItem, setSelectedContentItem] = useState<ContentItem | null>(null);

  const fieldName = field?.name;
  const isRequired = field?.validations?.some((v) => v.type === "required") ?? false;

  // Safe initial value — merge defaults with whatever state was passed
  const initialValue: ButtonValue = {
    ...DEFAULT_BUTTON_VALUE,
    ...(field?.default_value || {}),
    ...(state || {}),
  };

  // Restore selectedContentItem from existing state on mount/edit
  useEffect(() => {
    const content = state?.content || initialValue?.content;
    if (content?.Title) {
      setSelectedContentItem(content);
    }
  }, []);

  const handleOpenContentGallery = () => {
    setContentGalleryOpen(true);
  };

  const handleCloseContentGallery = () => {
    setContentGalleryOpen(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectedContent = (selectedData: any) => {
    if (selectedData?.slots?.length > 0) {
      const [selected] = selectedData.slots;

      const contentItem: ContentItem = {
        Title: selected?.Title || "",
        content_path: selected?.CurrentPageURL || selected?.path || "",
        content_name: selected?.page || selected?.Title || "",
        CurrentPageURL: selected?.CurrentPageURL || "",
        content_type: selected?.ContentType || "",
        is_hidden: false,
        type: "content",
      };

      setSelectedContentItem(contentItem);

      const updatedValue: ButtonValue = {
        ...buttonDataRef.current,
        content: contentItem,
      };

      onChangeRef.current(updatedValue);
      setState(updatedValue);
    }
    setContentGalleryOpen(false);
  };

  const handleRemoveContent = () => {
    setSelectedContentItem(null);
    const updatedValue: ButtonValue = {
      ...buttonDataRef.current,
      content: null,
    };

    onChangeRef.current(updatedValue);
    setState(updatedValue);
  };

  /**
   * Validate the ButtonValue based on the currently selected option.
   * Returns `true` if valid, or a JSON-encoded error object with per-field messages.
   */
  const validateButtonValue = useCallback((value: ButtonValue): true | string => {
    const errors: ButtonFieldErrors = {};

    // Display name is always required
    if (!value?.display_name || value.display_name.trim() === "") {
      errors.display_name = t("display_name_is_required");
    }

    // Option-specific required field
    switch (value?.selected_option) {
      case "link":
        if (!value.link_url || value.link_url.trim() === "") {
          errors.link_url = t("link_url_is_required");
        }
        break;
      case "page":
        if (!value.page_id || value.page_id.trim() === "") {
          errors.page_id = t("page_selection_is_required");
        }
        break;
      case "content":
        if (!value.content?.content_path || value.content.content_path.trim() === "") {
          errors.content = t("content_selection_is_required");
        }
        break;
      case "function":
        if (!value.function_name || value.function_name.trim() === "") {
          errors.function_name = t("function_selection_is_required");
        }
        break;
      default:
        break;
    }

    if (Object.keys(errors).length > 0) {
      return JSON.stringify(errors);
    }

    return true;
  }, []);

  const buildValidationRules = useCallback(() => {
    const rules: Record<string, unknown> = {};
    if (isRequired) {
      rules.validate = validateButtonValue;
    }
    return rules;
  }, [isRequired, validateButtonValue]);

  return (
    <Box id={`${field?.name}_id`}>
      <Controller
        name={fieldName}
        control={control}
        defaultValue={initialValue}
        rules={buildValidationRules()}
        render={({ field: controllerField, fieldState: { error } }) => {
          const buttonData: ButtonValue = {
            ...DEFAULT_BUTTON_VALUE,
            ...(controllerField.value || {}),
          };

          onChangeRef.current = controllerField.onChange;
          buttonDataRef.current = buttonData;

          // Parse the JSON error message into per-field errors
          const fieldErrors = parseFieldErrors(error?.message);
          const isDisplayNameError = !!fieldErrors.display_name;
          const isLinkUrlError = !!fieldErrors.link_url;
          const isPageError = !!fieldErrors.page_id;
          const isContentError = !!fieldErrors.content;
          const isFunctionError = !!fieldErrors.function_name;

          const handleFieldChange = <K extends keyof ButtonValue>(
            key: K,
            value: ButtonValue[K],
          ) => {
            const updatedValue: ButtonValue = { ...buttonData, [key]: value };
            if (key === "selected_option") {
              updatedValue.link_url = "";
              updatedValue.page_id = "";
              updatedValue.content = null;
              updatedValue.function_name = "";
              setSelectedContentItem(null);
            }
            controllerField.onChange(updatedValue);
            setState(updatedValue);

            // Re-trigger validation immediately after field change
            // so errors show/clear without waiting for form submit
            if (isRequired && error) {
              const result = validateButtonValue(updatedValue);
              if (result === true) {
                controllerField.onChange(updatedValue);
              }
            }
          };

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handleSelectPageUrlChange = (e: any) => {
            const { target: { value = "" } = {} } = e;
            handleFieldChange("page_id", value);
          };

          return (
            <Box sx={{ mb: 2, width: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Display Name — always required */}
                <Box>
                  <TextField
                    fullWidth
                    size='small'
                    placeholder={t("enter_display_name")}
                    value={buttonData.display_name}
                    disabled={isDisabled}
                    onChange={(e) => handleFieldChange("display_name", e.target.value)}
                    error={isDisplayNameError}
                    inputProps={{ maxLength: 15 }}
                  />
                  {isDisplayNameError && (
                    <FormHelperText error>{fieldErrors.display_name}</FormHelperText>
                  )}
                </Box>

                {/* Selected Option */}
                <FormControl fullWidth size='small'>
                  <Select
                    value={buttonData.selected_option}
                    disabled={isDisabled}
                    onChange={(e) =>
                      handleFieldChange(
                        "selected_option",
                        e.target.value as ButtonValue["selected_option"],
                      )
                    }>
                    {SELECTED_OPTION_LIST.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Page — required when selected_option is "page" */}
                {buttonData.selected_option === "page" && (
                  <Box>
                    <InternalPageList
                      pageUrl={buttonData.page_id}
                      dropDownMenuRef={dropDownMenuRef}
                      handleSelectPageUrlChange={handleSelectPageUrlChange}
                    />
                    {isPageError && <FormHelperText error>{fieldErrors.page_id}</FormHelperText>}
                  </Box>
                )}

                {/* Link — required when selected_option is "link" */}
                {buttonData.selected_option === "link" && (
                  <Box>
                    <TextField
                      fullWidth
                      size='small'
                      placeholder={t("enter_url")}
                      value={buttonData.link_url}
                      disabled={isDisabled}
                      onChange={(e) => handleFieldChange("link_url", e.target.value)}
                      error={isLinkUrlError}
                    />
                    {isLinkUrlError && (
                      <FormHelperText error>{fieldErrors.link_url}</FormHelperText>
                    )}
                  </Box>
                )}

                {/* Content — required when selected_option is "content" */}
                {buttonData.selected_option === "content" && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant='body2' sx={{ pb: "5px" }}>
                      {t("add_your_content")}
                    </Typography>
                    <Grid container sx={{ display: "flex", alignItems: "center" }}>
                      <Grid
                        item
                        xs={selectedContentItem ? 9 : 10}
                        sx={{
                          border: "1px solid",
                          borderColor: isContentError ? "error.main" : "divider",
                          borderRadius: 1,
                          p: 1,
                          minHeight: "40px",
                          display: "flex",
                          alignItems: "center",
                        }}>
                        {selectedContentItem ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 0.5,
                            }}>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>
                              {capitalizeFirstLetter(selectedContentItem.Title)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant='body2' color='text.secondary'>
                            {t("content_type_added")}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={selectedContentItem ? 1.5 : 2} sx={{ paddingLeft: "8px" }}>
                        <Tooltip title={selectedContentItem ? t("replace") : t("upload")}>
                          <Box
                            onClick={isDisabled ? undefined : handleOpenContentGallery}
                            sx={{
                              width: "40px",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#000",
                              borderRadius: 1,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              opacity: isDisabled ? 0.5 : 1,
                              "&:hover": {
                                opacity: isDisabled ? 0.5 : 0.85,
                              },
                            }}>
                            <img
                              alt={selectedContentItem ? t("replace") : t("upload")}
                              src={selectedContentItem ? replace : upload}
                              style={{ width: "20px", height: "20px" }}
                            />
                          </Box>
                        </Tooltip>
                      </Grid>

                      {selectedContentItem && (
                        <Grid item xs={1.5} sx={{ paddingLeft: "8px" }}>
                          <Tooltip title={t("remove_content")}>
                            <span>
                              <IconButton
                                size='small'
                                disabled={isDisabled}
                                onClick={handleRemoveContent}
                                sx={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: "#fff",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "error.main",
                                  color: "error.main",
                                  "&:hover": {
                                    backgroundColor: "error.main",
                                    color: "#fff",
                                  },
                                  "&.Mui-disabled": {
                                    borderColor: "action.disabled",
                                    color: "action.disabled",
                                  },
                                }}>
                                <DeleteOutline sx={{ fontSize: "20px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Grid>
                      )}
                    </Grid>
                    {isContentError && (
                      <FormHelperText error sx={{ mt: 0.5 }}>
                        {fieldErrors.content}
                      </FormHelperText>
                    )}
                  </Box>
                )}

                {/* Function — required when selected_option is "function" */}
                {buttonData.selected_option === "function" && (
                  <>
                    <Box>
                      <FormControl fullWidth size='small' error={isFunctionError}>
                        <Select
                          value={buttonData.function_name}
                          disabled={isDisabled}
                          displayEmpty
                          error={isFunctionError}
                          renderValue={(selected) => {
                            if (!selected)
                              return (
                                <span style={{ color: "#999" }}>{t("select_a_function")}</span>
                              );
                            return (
                              AVAILABLE_FUNCTIONS.find((fn) => fn.value === selected)?.label ||
                              selected
                            );
                          }}
                          onChange={(e) => handleFieldChange("function_name", e.target.value)}>
                          {AVAILABLE_FUNCTIONS.map((fn) => (
                            <MenuItem
                              key={fn.value}
                              value={fn.value}
                              onClick={(e) => e.stopPropagation()}>
                              {fn.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {isFunctionError && (
                          <FormHelperText error>{fieldErrors.function_name}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                    {buttonData.function_name &&
                      AVAILABLE_FUNCTIONS.some((fn) => fn.value === buttonData.function_name) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                            padding: "12px",
                          }}>
                          <Info sx={{ color: "#666", fontSize: "18px", flexShrink: 0 }} />
                          <Typography variant='body2'>
                            {
                              AVAILABLE_FUNCTIONS.find(
                                (fn) => fn.value === buttonData.function_name,
                              )?.info
                            }
                          </Typography>
                        </Box>
                      )}
                  </>
                )}

                <FormControl component='fieldset'>
                  <RadioGroup
                    row
                    value={buttonData.open_in_new_tab ? "new_tab" : "current_tab"}
                    onChange={(e) =>
                      handleFieldChange("open_in_new_tab", e.target.value === "new_tab")
                    }>
                    {TAB_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio size='small' />}
                        label={option.label}
                        disabled={isDisabled}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>
          );
        }}
      />

      <Dialog
        open={contentGalleryOpen}
        onClose={handleCloseContentGallery}
        fullScreen
        aria-labelledby='content-gallery-dialog'>
        {contentGalleryOpen && (
          <DynamicContentGallery
            handleSelectedContent={handleSelectedContent}
            onToggleContentGallery={handleCloseContentGallery}
            multi_select={false}
            single_select={true}
            allowed_collection={[]}
            allowed_content={["Articles", "Vod"]}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default ButtonField;
